import React, { useState } from 'react';
import DispenseStatementFilterOptionsDropdown from "./DispenseStatementFilterOptionsDropdown";
import { DispenseStatementFilterOption, DispenseStatementPageLabels, PillPackComFilterOption } from "../component";
import { AbortDownload } from "./DispenseStatementBottomSheet";
import fetchDocument, {
    StatementStatus,
    downloadTimeout,
    FetchReportError,
    processDocumentResponse,
    handleDownloadClick as utilHandleDownloadClick
} from "../common/utils/DocumentFetchUtils";

// URL Constants
const DISPENSE_STATEMENT_URL = '/your-orders-api/command/dispense-statement';
const PILLPACK_DISPENSE_STATEMENT_URL = '/your-orders-api/command/pillpack-dispense-statement';

const statementYearRegex = /%statementYear%/s;

declare global {
    interface Window {
        webkit: any;
        pharmacyBridge: any;
    }
}

interface DispenseStatementBottomSheetContentProps {
    labels: DispenseStatementPageLabels,
    filterOptions: DispenseStatementFilterOption[],
    pillPackComFilterOptions?: PillPackComFilterOption[],
    pillPackComMigrationStatus?: string;
    showBottomSheet(): void;
    hideBottomSheet(): void;
    abortDownload: AbortDownload;
    setStatementStatus(status: StatementStatus): void;
    statementStatus: StatementStatus;
    iosDevice?: boolean;
    androidDevice?: boolean;
    mShop?: boolean;
}

interface FilterOptionResult {
    option: DispenseStatementFilterOption | PillPackComFilterOption | undefined;
    source: 'pharmacy' | 'pillpack';
    actualValue: string;
}

function DispenseStatementBottomSheetContent(props: DispenseStatementBottomSheetContentProps) {
    const [downloadStatementError, setDownloadStatementError] = useState<FetchReportError>();

    const fileName = `${props.labels.medicationStatements}_`;

    const abortDownload = (reason: string) => {
        if (props.abortDownload.abortController) {
            props.abortDownload.reason = reason;
            props.abortDownload.abortController.abort();
        }
    }

    const lookupFilterOption = (filterOptionValue: string): FilterOptionResult => {
        if (filterOptionValue.startsWith('pillpack-')) {
            const actualValue = filterOptionValue.replace('pillpack-', '');
            const option = props.pillPackComFilterOptions?.find((option) => option.value === actualValue);
            return {
                option,
                source: 'pillpack',
                actualValue
            };
        }

        // Assume it's a Pharmacy option otherwise
        const actualValue = filterOptionValue.replace('pharmacy-', '');
        const option = props.filterOptions.find((option) => option.value === actualValue);
        return {
            option,
            source: 'pharmacy',
            actualValue
        };
    }

    const fetchStatement = async (filterOptionResult: FilterOptionResult): Promise<Blob> => {

        props.abortDownload.abortController = new AbortController();
        props.abortDownload.reason = undefined;

        const { signal } = props.abortDownload.abortController;
        setTimeout(() => {
            abortDownload("timeout");
        }, downloadTimeout);

        // Handle different parameter formats for Pharmacy vs PillPack options
        let filterParams = new URLSearchParams();

        if (filterOptionResult.option) {
            if (filterOptionResult.source === 'pharmacy' && 'parameterList' in filterOptionResult.option) {
                const filterOptionList = filterOptionResult.option.parameterList.map((parameter) => [parameter.key, parameter.value]);
                filterParams = new URLSearchParams(filterOptionList);
            } else if (filterOptionResult.source === 'pillpack') {
                filterParams = new URLSearchParams("year=" + filterOptionResult.option.value);
            }
        }

        // Create the fetch promise
        const dispenseStatementPromise = filterOptionResult.source === 'pillpack' ?
            fetchDocument(PILLPACK_DISPENSE_STATEMENT_URL, filterParams, { signal }) :
            fetchDocument(DISPENSE_STATEMENT_URL, filterParams, { signal });

        // Use the common processor - REPLACES the entire Promise chain
        return processDocumentResponse(
            dispenseStatementPromise,
            {
                filterOptionValue: filterOptionResult.actualValue,
                noStatementErrorMessage: props.labels.noStatementError.replace(statementYearRegex, filterOptionResult.actualValue),
                fileName: fileName + filterOptionResult.actualValue,
                mShop: props.mShop,
                iosDevice: props.iosDevice,
                androidDevice: props.androidDevice,
                downloadComplete: () => setStatus(StatementStatus.Done),
            },
            props.abortDownload
        );
    }

    const setStatus = (status: StatementStatus) => props.setStatementStatus(status);

    const handleDownloadClick = (filterOptionValue: string) => {
        const filterOptionResult = lookupFilterOption(filterOptionValue);
        utilHandleDownloadClick(
            filterOptionResult,
            fetchStatement,
            fileName + filterOptionResult.actualValue,
            setStatus,
            setDownloadStatementError,
            props.iosDevice,
            props.mShop,
        );
    }

    const handleDownloadAgainClick = async () => {
        if (!downloadStatementError) return;
        await handleDownloadClick(downloadStatementError.filterOptionValue);
    }

    const generateSection = (status: StatementStatus) => {
        return (
            <>
                {status === StatementStatus.FilterOptions &&
                    <DispenseStatementFilterOptionsDropdown
                        onDownloadClick={handleDownloadClick}
                        labels={props.labels}
                        filterOptions={props.filterOptions}
                        pillPackComFilterOptions={props.pillPackComFilterOptions}
                        pillPackComMigrationStatus={props.pillPackComMigrationStatus}
                        showChooseYear={true}
                    />
                }
                {status === StatementStatus.Downloading &&
                    <pui-section direction="vertical" >
                        <pui-section-column spacing="large" style={{ height: "24px" }}>
                            <pui-loading-indicator style={{ position: 'relative' }} />
                        </pui-section-column>
                        <pui-section-column overflow="true">
                            <pui-text spacingBottom="medium" input={props.labels.downloadProgress} />
                        </pui-section-column>
                    </pui-section>
                }
                {status === StatementStatus.Done && <>
                    <pui-section flowdirection="vertical" >
                        <pui-section-column spacing="large">
                            <div className="pui-icon status-success-icon" style={{ display: "flex", justifyContent: "center", height: "32px", width: "32px" }} />
                        </pui-section-column>
                        <pui-section-column>
                            <pui-text spacingBottom="medium" input={props.labels.downloadSuccess} />
                        </pui-section-column>
                        <pui-section-column>
                            <pui-button
                                id="button-to-confirm-dispense-statement-download"
                                data-csa-c-type="button"
                                data-csa-c-action="confirm"
                                data-csa-c-content-id="button-to-confirm-dispense-statement-download"
                                data-csa-c-slot-id="dispense-statement"
                                label={props.labels.continueButton}
                                spacingTop="small"
                                textColor="black-color"
                                textSize="extra-large"
                                onClick={props.hideBottomSheet}
                            />
                        </pui-section-column>
                    </pui-section>
                </>
                }
                {status === StatementStatus.GenerationError && <>
                    <pui-section flowdirection="vertical" >
                        <pui-section-column spacing="large">
                            <div className="pui-icon status-error-icon" style={{ display: "flex", justifyContent: "center", height: "32px", width: "32px" }} />
                        </pui-section-column>
                        <pui-section-column>
                            <pui-text spacingBottom="medium" input={props.labels.downloadError} />
                        </pui-section-column>
                        <pui-section-column>
                            <pui-button
                                id="button-to-confirm-dispense-statement-error"
                                data-csa-c-type="button"
                                data-csa-c-action="confirm"
                                data-csa-c-content-id="button-to-confirm-dispense-statement-error"
                                data-csa-c-slot-id="dispense-statement"
                                label={props.labels.downloadAgain}
                                spacingTop="small"
                                textColor="black-color"
                                textSize="extra-large"
                                onClick={handleDownloadAgainClick}
                            />
                        </pui-section-column>
                    </pui-section>
                </>
                }
                {status === StatementStatus.NotFoundError && <>
                    <DispenseStatementFilterOptionsDropdown
                        labels={props.labels}
                        filterOptions={props.filterOptions}
                        pillPackComFilterOptions={props.pillPackComFilterOptions}
                        pillPackComMigrationStatus={props.pillPackComMigrationStatus}
                        onDownloadClick={handleDownloadClick}
                        alertMessage={downloadStatementError?.message}
                        showChooseYear={true}
                    />
                </>
                }
            </>
        );
    };
    return (<div className="dispense-statement__modal">
        <pui-section>
            <pui-heading
                input={props.labels.medicationStatements}
                textSize="medium"
                style={{ marginBottom: "4px" }}
            />
            {generateSection(props.statementStatus)}
        </pui-section>
    </div>);
}

export default DispenseStatementBottomSheetContent;
